import { useMemo, useRef } from "react";
import { WEEKDAYS, colorFor, isoDate } from "../shared";

const DOUBLE_CLICK_MS = 260;

/** Build the 42-cell 6×7 grid for a given month cursor (Mon-first). */
const useDayCells = (cursor) =>
  useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startWeekday = (first.getDay() + 6) % 7; // 0..6 with Monday=0
    const start = new Date(first);
    start.setDate(first.getDate() - startWeekday);
    const cells = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

const DayCell = ({
  date,
  inMonth,
  dayEvents,
  isToday,
  iso,
  onOpenDay,
  onOpenEvent,
  onDoubleClickEvent,
}) => {
  // Timer-based click / double-click disambiguation. A single click waits
  // ~260ms before firing so a second click within that window cancels it
  // and routes to the double-click handler instead. Without this, React's
  // onDoubleClick fires AFTER two onClick events — the event-details
  // modal has already opened by then, masking the "flip to dark-blue"
  // effect the admin expects.
  const clickTimers = useRef({});
  const handleEventClick = (e) => {
    const pending = clickTimers.current[e.id];
    if (pending) {
      clearTimeout(pending);
      delete clickTimers.current[e.id];
      if (onDoubleClickEvent) onDoubleClickEvent(e);
      return;
    }
    clickTimers.current[e.id] = setTimeout(() => {
      delete clickTimers.current[e.id];
      onOpenEvent(e);
    }, DOUBLE_CLICK_MS);
  };

  return (
  <div
    data-testid={`cal-day-${iso}`}
    className={
      "min-h-[112px] border-t border-l border-[var(--ld-border)] -mt-px -ml-px p-2 " +
      (inMonth ? "bg-transparent" : "bg-[var(--ld-ivory-warm)]/20")
    }
  >
    <div className="flex items-center justify-between">
      <button
        type="button"
        onClick={() => onOpenDay({ iso, events: dayEvents })}
        title={
          dayEvents.length
            ? `View all ${dayEvents.length} slot${
                dayEvents.length === 1 ? "" : "s"
              } on this day`
            : "No slots on this day"
        }
        data-testid={`cal-day-button-${iso}`}
        className={
          "inline-flex items-center justify-center text-[12px] transition-colors " +
          (isToday
            ? "h-6 w-6 rounded-full bg-[var(--ld-ink)] text-[var(--ld-gold)]"
            : inMonth
            ? "text-[var(--ld-ink)] hover:text-[var(--ld-gold-deep)]"
            : "text-[var(--ld-ink-muted)]/60 hover:text-[var(--ld-ink)]")
        }
      >
        {date.getDate()}
      </button>
      {dayEvents.length > 0 && (
        <span
          className="text-[9px] tracking-[0.15em] uppercase text-[var(--ld-gold-deep)] font-medium"
          title={`${dayEvents.length} slot${
            dayEvents.length === 1 ? "" : "s"
          }`}
        >
          {dayEvents.length}
        </span>
      )}
    </div>
    <div className="mt-1 space-y-1">
      {dayEvents.slice(0, 3).map((e) => {
        const c = colorFor(e);
        return (
          <button
            key={e.id}
            type="button"
            onClick={() => handleEventClick(e)}
            data-testid={`cal-event-${e.id}`}
            title={
              (e.title || c.label) +
              (e.time ? ` · ${e.time}` : "") +
              " · double-click to mark booked"
            }
            className={
              "w-full text-left px-2 py-1 text-[11px] leading-tight border-l-2 truncate hover:opacity-90 " +
              c.card
            }
          >
            {e.time ? (
              <span className="font-medium mr-1">{e.time}</span>
            ) : null}
            {e.title || c.label}
          </button>
        );
      })}
      {dayEvents.length > 3 && (
        <button
          type="button"
          data-testid={`cal-day-more-${iso}`}
          onClick={() => onOpenDay({ iso, events: dayEvents })}
          className="block w-full text-left px-2 py-1 text-[10px] tracking-[0.18em] uppercase text-[var(--ld-gold-deep)] hover:text-[var(--ld-ink)]"
        >
          + {dayEvents.length - 3} more · view all
        </button>
      )}
    </div>
  </div>
  );
};

const CalendarGrid = ({
  cursor,
  eventsByDate,
  onOpenDay,
  onOpenEvent,
  onDoubleClickEvent,
}) => {
  const days = useDayCells(cursor);
  const todayIso = isoDate(new Date());
  return (
    <div className="mt-4 border border-[var(--ld-border)]">
      <div className="grid grid-cols-7 bg-[var(--ld-ivory-warm)]/50 border-b border-[var(--ld-border)]">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] text-center"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const iso = isoDate(d);
          return (
            <DayCell
              key={iso}
              date={d}
              iso={iso}
              inMonth={d.getMonth() === cursor.getMonth()}
              isToday={iso === todayIso}
              dayEvents={eventsByDate[iso] || []}
              onOpenDay={onOpenDay}
              onOpenEvent={onOpenEvent}
              onDoubleClickEvent={onDoubleClickEvent}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CalendarGrid;
